import { useEffect, useState } from 'react';
import { Box, MenuItem, Tab, Tabs } from '@mui/material';

import {
  CustomAutoComplete,
  CustomButton,
  CustomCheckbox,
  CustomTextField,
  DrawerForm,
  DrawerFormActionSummary,
  DrawerFormActions,
} from '../../../common/components';
import { CURRENCY_OPTIONS } from '../../constants/financeConstants';
import { formatCurrencyAmount, parseNumber } from '../../utils/financeFormatters';
import {
  CASH_ACCOUNT_DRAWER_TABS,
  INITIAL_CASH_ACCOUNT_FORM,
} from '../constants/cashAccountsConstants';
import {
  buildCashAccountForm,
  buildCreateCashAccountPayload,
  buildUpdateCashAccountPayload,
  validateCashAccountForm,
} from '../utils/cashAccountsUtils';

const gridSx = {
  display: 'grid',
  gridTemplateColumns: {
    xs: '1fr',
    sm: 'repeat(2, minmax(0, 1fr))',
    md: 'repeat(12, minmax(0, 1fr))',
  },
  columnGap: 1.25,
  rowGap: 1.25,
  alignItems: 'start',
  px: { xs: 1.25, md: 2 },
  py: { xs: 1.25, md: 1.5 },
  minWidth: 0,
};

const CashAccountDrawer = ({
  open,
  onClose,
  editMode,
  selectedAccount,
  onCreate,
  onUpdate,
  isSubmitting,
}) => {
  const [formData, setFormData] = useState(INITIAL_CASH_ACCOUNT_FORM);
  const [errors, setErrors] = useState({});
  const [activeDrawerTab, setActiveDrawerTab] = useState(CASH_ACCOUNT_DRAWER_TABS.data);

  useEffect(() => {
    if (!open) return;
    setFormData(editMode && selectedAccount
      ? buildCashAccountForm(selectedAccount)
      : INITIAL_CASH_ACCOUNT_FORM);
    setErrors({});
    setActiveDrawerTab(CASH_ACCOUNT_DRAWER_TABS.data);
  }, [open, editMode, selectedAccount]);

  const clearFieldError = (name) => {
    if (!errors[name]) return;
    setErrors((prev) => {
      const updated = { ...prev };
      delete updated[name];
      return updated;
    });
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    clearFieldError(name);
  };

  const handleActiveChange = (event) => {
    setFormData((prev) => ({ ...prev, is_active: event.target.checked }));
  };

  const handleSubmit = async () => {
    const validationErrors = validateCashAccountForm(formData, editMode);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      if (editMode && formData.uuid) {
        await onUpdate(formData.uuid, buildUpdateCashAccountPayload(formData));
      } else {
        await onCreate(buildCreateCashAccountPayload(formData));
      }
      onClose();
    } catch (error) {
      console.error('Error al guardar la caja:', error);
    }
  };

  const summaryValue = editMode
    ? formatCurrencyAmount(formData.current_balance, formData.currency)
    : formatCurrencyAmount(parseNumber(formData.initial_balance), formData.currency);

  const dataFields = (
    <Box sx={gridSx}>
      <CustomTextField
        label="Nombre"
        name="name"
        value={formData.name}
        onChange={handleChange}
        required
        error={Boolean(errors.name)}
        helperText={errors.name}
        sx={{ gridColumn: { xs: '1', sm: '1 / -1', md: 'span 12' } }}
      />

      <CustomAutoComplete
        label="Moneda"
        name="currency"
        value={formData.currency}
        onChange={handleChange}
        disabled={editMode}
        required
        error={Boolean(errors.currency)}
        helperText={errors.currency}
        sx={{ gridColumn: { xs: '1', sm: 'span 1', md: 'span 6' } }}
      >
        {CURRENCY_OPTIONS.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </CustomAutoComplete>

      <CustomTextField
        label="Saldo inicial"
        name="initial_balance"
        value={formData.initial_balance}
        onChange={handleChange}
        disabled={editMode}
        required
        error={Boolean(errors.initial_balance)}
        helperText={errors.initial_balance}
        sx={{ gridColumn: { xs: '1', sm: 'span 1', md: 'span 6' } }}
      />

      {!editMode && formData.currency === 'USD' && (
        <CustomTextField
          label="Cotización ARS"
          name="exchange_rate_to_ars"
          value={formData.exchange_rate_to_ars}
          onChange={handleChange}
          required
          error={Boolean(errors.exchange_rate_to_ars)}
          helperText={errors.exchange_rate_to_ars}
          sx={{ gridColumn: { xs: '1', sm: 'span 1', md: 'span 6' } }}
        />
      )}
    </Box>
  );

  const statusFields = (
    <Box sx={gridSx}>
      <CustomTextField
        label="Saldo actual"
        value={formatCurrencyAmount(formData.current_balance, formData.currency)}
        disabled
        sx={{ gridColumn: { xs: '1', sm: 'span 1', md: 'span 6' } }}
      />
      <Box
        sx={{
          gridColumn: { xs: '1', sm: 'span 1', md: 'span 6' },
          display: 'flex',
          alignItems: 'center',
          minHeight: 68,
          pt: 1,
        }}
      >
        <CustomCheckbox
          label="Caja activa"
          checked={Boolean(formData.is_active)}
          onChange={handleActiveChange}
        />
      </Box>
    </Box>
  );

  return (
    <DrawerForm
      open={open}
      onClose={onClose}
      title={editMode ? 'Editar caja' : 'Nueva caja'}
      width={{ xs: '100vw', md: 'min(620px, calc(100vw - 40px))' }}
      contentSx={{ backgroundColor: 'background.paper' }}
      footer={(
        <DrawerFormActions
          summary={(
            <DrawerFormActionSummary
              label={editMode ? 'Saldo actual' : 'Saldo inicial'}
              value={summaryValue}
            />
          )}
        >
          <CustomButton variant="outlined" onClick={onClose} disabled={isSubmitting}>
            Cancelar
          </CustomButton>
          <CustomButton
            variant="base"
            onClick={handleSubmit}
            isLoading={isSubmitting}
            disabled={!formData.name.trim()}
          >
            {editMode ? 'Actualizar' : 'Crear'}
          </CustomButton>
        </DrawerFormActions>
      )}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1, md: 1.25 }, minWidth: 0 }}>
        {editMode && (
          <Box sx={{ backgroundColor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={activeDrawerTab}
              onChange={(_, value) => setActiveDrawerTab(value)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                minHeight: 44,
                '& .MuiTab-root': {
                  minHeight: 44,
                  fontSize: 13,
                  fontWeight: 700,
                },
              }}
            >
              <Tab value={CASH_ACCOUNT_DRAWER_TABS.data} label="Datos de la caja" />
              <Tab value={CASH_ACCOUNT_DRAWER_TABS.status} label="Estado y saldo" />
            </Tabs>
          </Box>
        )}
        {(!editMode || activeDrawerTab === CASH_ACCOUNT_DRAWER_TABS.data) && dataFields}
        {editMode && activeDrawerTab === CASH_ACCOUNT_DRAWER_TABS.status && statusFields}
      </Box>
    </DrawerForm>
  );
};

export default CashAccountDrawer;
